import { Alert, AlertTitle, Card, CardHeader, Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useToast } from "randmarcomps";
import { CreateOpportunityInput, usePutV4ManufacturerByRouteManufacturerIdOpportunityMutation } from "randmar-api-client";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { setUserSelectedOpportunity } from "../utils/LocalStorageUtils";
import withRoot from "../withRoot";

interface IProps {
  applicationId: string;
}

const CreateOpportunity: React.FC<IProps> = ({ applicationId }) => {
  const { toast } = useToast();
  const [addOpportunity, { isLoading }] = usePutV4ManufacturerByRouteManufacturerIdOpportunityMutation();

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bidNumber, setBidNumber] = useState("");
  const [bidTitle, setBidTitle] = useState("");
  const [quoteOnly, setQuoteOnly] = useState(false);
  const [description, setDescription] = useState("");
  const [defaultResellers, setDefaultResellers] = useState("");


  const handleConfirmRequest = async () => {
    const opportunityData: CreateOpportunityInput = {
      BidNumber: bidNumber,
      BidTitle: bidTitle,
      StartDate: startDate,
      EndDate: endDate,
      QuoteOnly: quoteOnly,
      Description: description,
      DefaultResellers: defaultResellers,
    };

    try {
      await addOpportunity({ routeManufacturerId: applicationId, createOpportunityInput: opportunityData }).unwrap();
      toast({ variant: "success", title: "Success!", description: "Opportunity created." });
      setUserSelectedOpportunity(bidNumber);
      setOpen(true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Opportunity could not be created." });
    }
  }

  const handleCancel = () => {
    setOpen(false);
    setStartDate("");
    setEndDate("");
    setBidNumber("");
    setBidTitle("");
    setQuoteOnly(false);
    setDescription("");
    setDefaultResellers("");
  }

  const propertyChange = (propertyName: string, newValue: string) => {
    switch (propertyName) {
      case "startDate": setStartDate(newValue);
        break;
      case "endDate": setEndDate(newValue);
        break;
      case "bidNumber": setBidNumber(newValue);
        break;
      case "bidTitle": setBidTitle(newValue);
        break;
      case "quoteOnly": setQuoteOnly(newValue === 'true');
        break;
      case "description": setDescription(newValue);
        break;
      case "defaultResellers":
        let newDefaultResellersValue = defaultResellers;
        if (defaultResellers.includes(newValue))
          newDefaultResellersValue = newDefaultResellersValue.replace(newValue + ",", "");
        else
          newDefaultResellersValue = newDefaultResellersValue + newValue + ",";

        setDefaultResellers(newDefaultResellersValue);
        break;
    }
  }

  return (
    <>
      <CardHeader
        title={<Typography variant="h1">Create New Opportunity</Typography>}
        subheader="Basic Information"
      />
      <Card>
        <Stack direction='column' spacing={2}>
          <Stack direction='row' spacing={2}>
            <TextField
              required
              id="bidNumber"
              label="Opportunity Number"
              type="text"
              value={bidNumber}
              onChange={e => propertyChange("bidNumber", e.target.value as string)}
            />
            <FormControlLabel
              label="Quote Only"
              control={<Checkbox checked={quoteOnly} onChange={(_, newValue) => propertyChange("quoteOnly", newValue.toString())} id="quoteOnly" />}
            />
          </Stack>
          <TextField
            required
            id="bidTitle"
            label="Opportunity Title"
            type="text"
            value={bidTitle}
            fullWidth
            sx={{ marginTop: "5px" }}
            onChange={e => propertyChange("bidTitle", e.target.value as string)}
          />
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="center" alignItems="center" spacing={2}>
            <TextField
              required
              id="startDate"
              label="Start Date"
              type="date"
              value={startDate}
              fullWidth
              onChange={e => propertyChange("startDate", e.target.value as string)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              required
              id="endDate"
              label="End Date"
              type="date"
              value={endDate}
              fullWidth
              onChange={e => propertyChange("endDate", e.target.value as string)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            id="description"
            label="Description - Optional"
            type="text"
            value={description}
            placeholder="Any specific information like end-user requirements or minimum ordering number, etc."
            fullWidth
            onChange={e => propertyChange("description", e.target.value as string)}
            multiline
            rows={4}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Card>

      <CardHeader
        sx={{ mt: 2 }}
        title={<Typography variant="h1">Default Resellers - Optional</Typography>}
        subheader="Choosing one or multiple of these options will add all your qualified resellers from these margin (pricing) packages."
      />
      <Alert severity="info" sx={{ m: 2, mt: 0 }}>
        <AlertTitle color="#3b6cca">Targeted Resellers</AlertTitle>
        If you did not select Default Resellers, you will need to manually add them to this opportunity. Search by reseller (in the search bar) and click on its Actions button.
      </Alert>
      <FormGroup sx={{ m: 3, mt: 1 }}>
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Exclusive")} checked={defaultResellers.includes("Exclusive")} />} label="Exclusive - Qualified resellers in the Exclusive Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "E-Commerce")} checked={defaultResellers.includes("E-Commerce")} />} label="E‑Commerce - Qualified resellers in the E-Commerce Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Commercial")} checked={defaultResellers.includes("Commercial")} />} label="Commercial - Qualified resellers in the Commercial Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Academy")} checked={defaultResellers.includes("Academy")} />} label="Academy - Qualified Academy resellers in the Buying Group Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Novexco")} checked={defaultResellers.includes("Novexco")} />} label="Novexco - Qualified Novexco resellers in the Buying Group Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "COOPSCO")} checked={defaultResellers.includes("COOPSCO")} />} label="COOPSCO - Qualified COOPSCO resellers in the Buying Group Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Jean Coutu")} checked={defaultResellers.includes("Jean Coutu")} />} label="Jean Coutu - Qualified Jean Coutu resellers in the Buying Group Package" />
        <FormControlLabel sx={{ p: 0.5 }} control={<Checkbox onChange={() => propertyChange("defaultResellers", "Starter")} checked={defaultResellers.includes("Starter")} />} label="Starter - Qualified resellers in the Starter Package" />
      </FormGroup>

      <Button
        className="ml-3"
        size="lg"
        onClick={handleConfirmRequest}
        disabled={!startDate || !endDate || !bidTitle || !bidNumber || startDate > endDate || isLoading}
      >
        Create New Opportunity
      </Button>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Success!</DialogTitle>
            <DialogDescription>
              Your opportunity has been successfully created.
            </DialogDescription>
          </DialogHeader>
          <Stack direction='column' spacing={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle color="#3b6cca">And Then?</AlertTitle>
              Now you need to add products to this opportunity! Search by product and click on its Actions button.
              Also, if you did not select Default Resellers, you will need to manually add them to this opportunity, just like with products!
            </Alert>
            <div>Where to next?</div>
            <Link to={Path.opportunity(applicationId, bidNumber)} style={{ textAlign: "center" }}>
              <Button>
                Go to {bidNumber}
              </Button>
            </Link>
          </Stack>
          <DialogFooter className="flex justify-between pt-4">
            <Link to={Path.opportunities(applicationId)}>
              <Button variant="outline">
                View All Opportunities
              </Button>
            </Link>
            <Button onClick={handleCancel} variant="outline">
              Create another Opportunity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default withRoot(withManufacturer(CreateOpportunity));
